import { Photo } from '@mui/icons-material'
import Dropzone, { DropzoneProps } from './Dropzone'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import Cropper, { Area } from 'react-easy-crop'
import { getCroppedImageBlob, prepareImageForCrop } from '../utils/imageCrop'
import useDialog from '../hooks/useDialog'

export interface AssetPhotoDropzoneProps extends Omit<DropzoneProps, 'onDrop'> {
  value?: File | null
  onChange?: (file: File | null) => void
}

export default function AssetPhotoDropzone({
  value,
  label,
  buttonText,
  onChange,
  ...props
}: AssetPhotoDropzoneProps) {
  /**
   * The tranlate function.
   */
  const { t } = useTranslation(['asset', 'common'])

  /**
   * The image URL.
   */
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const {
    open: isCropDialogOpen,
    openDialog: openCropDialog,
    closeDialog: closeCropDialog
  } = useDialog()
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [cropMode, setCropMode] = useState<'normal' | 'square'>('normal')
  const [imageAspect, setImageAspect] = useState<number>(1)

  async function handleDrop(files: File[]) {
    if (files.length > 0 && files[0].type.startsWith('image/')) {
      let file = files[0]
      try {
        file = await prepareImageForCrop(file)
      } catch (_error) {
        file = files[0]
      }

      setSelectedFile(file)
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setCroppedAreaPixels(null)
      setCropMode('normal')

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }

      setPreviewUrl(URL.createObjectURL(file))
      openCropDialog()
    }
  }

  function handleRemove() {
    onChange?.(null)
  }

  const handleCropComplete = (_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels)
  }

  const closeCropDialogAndReset = () => {
    closeCropDialog()
    setSelectedFile(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    setPreviewUrl(null)
    setCropMode('normal')
    setImageAspect(1)
  }

  function handleCropModeChange(_event: unknown, mode: 'normal' | 'square' | null): void {
    if (mode === null) {
      return
    }

    setCropMode(mode)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
  }

  const handleCropSave = async () => {
    if (!selectedFile) {
      closeCropDialogAndReset()
      return
    }

    if (!previewUrl || !croppedAreaPixels) {
      closeCropDialogAndReset()
      return
    }

    try {
      const blob = await getCroppedImageBlob(previewUrl, croppedAreaPixels, selectedFile.type)
      const file = new File([blob], selectedFile.name, {
        type: selectedFile.type,
        lastModified: Date.now()
      })
      onChange?.(file)
      closeCropDialogAndReset()
    } catch (_error) {
      closeCropDialogAndReset()
    }
  }

  useEffect(() => {
    let objectUrl: string | null = null
    if (value) {
      objectUrl = URL.createObjectURL(value)
      setImageUrl(objectUrl)
    } else {
      setImageUrl(null)
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [value])

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  useEffect(() => {
    if (!previewUrl) {
      setImageAspect(1)
      return
    }

    let cancelled = false
    const image = new Image()

    image.onload = () => {
      if (!cancelled && image.naturalWidth > 0 && image.naturalHeight > 0) {
        setImageAspect(image.naturalWidth / image.naturalHeight)
      }
    }

    image.src = previewUrl

    return () => {
      cancelled = true
    }
  }, [previewUrl])

  return (
    <>
      {imageUrl ? (
        <>
          <Box
            minHeight={200}
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              borderRadius: 1,
              width: '100%',
              cursor: 'pointer'
            }}
          >
            <img src={imageUrl} style={{ width: '100%', height: 'auto' }} />
          </Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button variant="text" color="error" onClick={handleRemove}>
              {t('removePhoto')}
            </Button>
          </Stack>
        </>
      ) : (
        <Dropzone
          icon={<Photo color="disabled" sx={{ fontSize: 48 }} />}
          label={label ?? t('takePhotoOfTheInstalledAsset')}
          buttonText={buttonText ?? t('selectPhoto')}
          onDrop={handleDrop}
          {...props}
        />
      )}
      <Dialog open={isCropDialogOpen} onClose={closeCropDialogAndReset} fullWidth maxWidth="sm">
        <DialogTitle>{t('common:previewPhoto')}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 320,
              bgcolor: 'grey.900',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {previewUrl && (
              <Cropper
                image={previewUrl}
                crop={crop}
                zoom={zoom}
                aspect={cropMode === 'square' ? 1 : imageAspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={handleCropComplete}
              />
            )}
          </Box>
          <Box sx={{ mt: 3, px: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Rajaus
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={cropMode}
              onChange={handleCropModeChange}
            >
              <ToggleButton value="normal">Normaali</ToggleButton>
              <ToggleButton value="square">Neliö</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ mt: 3, px: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('common:zoom')}
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, value) => setZoom(value as number)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCropDialogAndReset} color="inherit">
            {t('common:cancel')}
          </Button>
          <Button onClick={handleCropSave} variant="contained">
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
